import {
  LayoutDashboard,
  BarChart3,
  MousePointerClick,
  Users,
  DollarSign,
  Globe2,
  Activity,
  type LucideIcon,
} from 'lucide-react';
import type { DashboardSection } from '@/types/dashboard';

/* ============================================================================
   Navigation item definition
   ============================================================================ */

export interface NavItem {
  id: DashboardSection;
  label: string;
  icon: LucideIcon;
  description: string;
}

export const NAV_ITEMS: NavItem[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboard,
    description: 'Executive summary and KPIs',
  },
  {
    id: 'acquisition',
    label: 'Acquisition',
    icon: Globe2,
    description: 'Traffic sources and channels',
  },
  {
    id: 'engagement',
    label: 'Engagement',
    icon: MousePointerClick,
    description: 'User engagement and behavior',
  },
  {
    id: 'conversions',
    label: 'Conversions',
    icon: BarChart3,
    description: 'Booking funnel and conversions',
  },
  {
    id: 'audience',
    label: 'Audience',
    icon: Users,
    description: 'Demographics and geography',
  },
  {
    id: 'revenue',
    label: 'Revenue',
    icon: DollarSign,
    description: 'Revenue analytics and trends',
  },
  {
    id: 'website_performance',
    label: 'Site Performance',
    icon: Activity,
    description: 'Scroll depth, clicks, and user flows',
  },
];

/* ============================================================================
   Navigation Component
   ============================================================================ */

interface NavigationProps {
  activeSection: DashboardSection;
  onNavigate: (section: DashboardSection) => void;
  collapsed?: boolean;
}

const KEYBOARD_SHORTCUTS = ['1', '2', '3', '4', '5', '6', '7'];

export default function Navigation({
  activeSection,
  onNavigate,
  collapsed = false,
}: NavigationProps) {
  return (
    <nav className="flex flex-col gap-1">
      {NAV_ITEMS.map((item, index) => {
        const Icon = item.icon;
        const isActive = activeSection === item.id;
        const shortcutKey = KEYBOARD_SHORTCUTS[index];

        return (
          <button
            key={item.id}
            onClick={() => onNavigate(item.id)}
            title={collapsed ? `${item.label} (${shortcutKey})` : item.description}
            className={`
              group relative
              w-full flex items-center gap-3
              ${collapsed ? 'px-0 py-2.5 justify-center' : 'px-3 py-2.5'}
              rounded-lg
              text-sm font-medium
              transition-colors duration-150
              ${
                isActive
                  ? 'text-text bg-bg-2'
                  : 'text-text-3 hover:text-text-2 hover:bg-bg-1'
              }
            `}
          >
            {/* Active indicator — gold left bar */}
            {isActive && (
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-monarch-gold rounded-r-full" />
            )}

            <Icon
              className={`
                flex-shrink-0 w-[18px] h-[18px] transition-colors
                ${isActive ? 'text-monarch-gold' : 'text-current'}
              `}
            />

            {!collapsed && (
              <span className="whitespace-nowrap flex-1 text-left">{item.label}</span>
            )}

            {!collapsed && shortcutKey && (
              <kbd
                className={`
                  px-1.5 py-0.5 rounded text-[10px] font-mono
                  ${
                    isActive
                      ? 'bg-monarch-gold/10 text-monarch-gold border border-monarch-gold/20'
                      : 'bg-bg-1 text-text-3 border border-border group-hover:text-text-3'
                  }
                `}
              >
                {shortcutKey}
              </kbd>
            )}
          </button>
        );
      })}
    </nav>
  );
}
